import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronRight, Flame, TrendingUp, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useStacks } from '@/lib/hooks';
import { STACK_ICONS, STACK_COLORS } from '@/lib/store';
import { Button } from '@/components/ui/button';
import { SevenDayStrip } from '@/components/SevenDayStrip';
import { EmptyState } from '@/components/EmptyState';

export function StacksPage() {
    const navigate = useNavigate();
    const { activeStacks, getStackProgress, isPro, freeTierLimit, isLoading } = useStacks();

    const calculateRunRate = (history) => {
        if (!history || history.length === 0) return 0;
        const ran = history.filter(d => d.ran).length;
        return Math.round((ran / history.length) * 100);
    };

    if (isLoading) {
        return (
            <div className="max-w-lg mx-auto px-4 py-6">
                <header className="flex items-center justify-between mb-6">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground">Stacks</h1>
                        <div className="h-4 w-16 bg-muted rounded mt-1 animate-pulse" />
                    </div>
                    <div className="h-9 w-16 bg-muted rounded animate-pulse" />
                </header>
                <div className="space-y-2">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto px-4 py-6">
            {/* Header */}
            <header className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Stacks</h1>
                    <p className="text-sm text-muted-foreground">
                        {activeStacks.length} active
                    </p>
                </div>
                <Button
                    onClick={() => navigate('/stacks/new')}
                    size="sm"
                    className="gap-1.5"
                >
                    <Plus className="w-4 h-4" />
                    New
                </Button>
            </header>

            {/* Stacks list */}
            {activeStacks.length > 0 ? (
                <div className="space-y-2">
                    {activeStacks.map((stack, index) => {
                        const progress = getStackProgress(stack.id);
                        const icon = STACK_ICONS.find(i => i.name === stack.icon) || STACK_ICONS[0];
                        const color = STACK_COLORS.find(c => c.name === stack.color) || STACK_COLORS[0];
                        const runRate = calculateRunRate(progress?.history7Day);
                        const streak = progress?.streak || 0;

                        return (
                            <button
                                key={stack.id}
                                onClick={() => navigate(`/stacks/${stack.id}`)}
                                className={cn(
                                    "w-full flex items-center gap-3 p-3 rounded-lg bg-card border border-border text-left",
                                    "transition-all duration-150 hover:border-primary/30 hover:shadow-soft active:scale-[0.99]",
                                    "animate-slide-up"
                                )}
                                style={{ animationDelay: `${index * 0.03}s` }}
                            >
                                {/* Icon */}
                                <div className={cn(
                                    "w-10 h-10 rounded-lg flex items-center justify-center text-xl shrink-0",
                                    color.lightClass
                                )}>
                                    {icon.emoji}
                                </div>

                                {/* Content */}
                                <div className="flex-1 min-w-0">
                                    <h3 className="font-semibold text-foreground truncate text-sm">
                                        {stack.name}
                                    </h3>

                                    {/* Stats row */}
                                    <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                                        {streak > 0 && (
                                            <span className="inline-flex items-center gap-1">
                                                <Flame className={cn(
                                                    "w-3 h-3",
                                                    streak >= 7 ? "text-warning" : "text-muted-foreground"
                                                )} />
                                                {streak}d
                                            </span>
                                        )}
                                        <span className="inline-flex items-center gap-1">
                                            <TrendingUp className="w-3 h-3" />
                                            {runRate}%
                                        </span>
                                    </div>
                                </div>

                                {/* 7-day strip */}
                                <SevenDayStrip
                                    history={progress?.history7Day || []}
                                    colorName={stack.color}
                                    size="small"
                                    showLabels={false}
                                />

                                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                            </button>
                        );
                    })}
                </div>
            ) : (
                <EmptyState
                    icon={Plus}
                    title="No stacks yet"
                    description="Create your first stack to start building habits."
                    actionLabel="Create Stack"
                    onAction={() => navigate('/stacks/new')}
                />
            )}

            {/* Free tier notice */}
            {!isPro && activeStacks.length >= freeTierLimit && (
                <div className="mt-4 p-3 rounded-lg bg-accent border border-accent text-sm text-accent-foreground animate-slide-up">
                    Free tier: {freeTierLimit} stacks max. Upgrade to Pro for unlimited.
                </div>
            )}
        </div>
    );
}
